import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  getProduct,
  getLowStockProducts,
  deleteProduct,
  exportProducts,
} from "../../api/productService";
import { getErrorMessage } from "../../utils/errorHandler";
import Pagination from "../Pagination/Pagination";
import ActionIconButton from "../Shared/ActionIconButton";
import { FaEye, FaEdit, FaTrash } from "react-icons/fa";
import ExportCsvButton from "../../common/ExportCsvButton";
import FullPageLoader from "../../common/FullPageLoader";
import EmptyState from "../../common/EmptyState";
import { Checkbox } from "../../common/FormControls";

export default function ProductList() {
  const [products, setProducts] = useState([]);
  const [sampleFileUrl, setSampleFileUrl] = useState("");
  const [loading, setLoading] = useState(false);
  // SERVER PAGINATION
  const [currentPage, setCurrentPage] = useState(1);
  const [searchParams, setSearchParams] = useSearchParams();
  const lowStockEnabled = searchParams.get("lowStock") === "true";
  const itemsPerPage = lowStockEnabled ? 10 : 9;
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const navigate = useNavigate();

  const handleExportProduct = async () => {
    try {
      const response = await exportProducts();

      const blob = new Blob([response.data], {
        type: "text/csv",
      });

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = "products.csv";

      document.body.appendChild(link);
      link.click();

      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Export failed:", error);
    }
  };

  // FETCH PRODUCTS
  const fetchProducts = async (page = 1) => {
    setLoading(true);

    try {
      const res = lowStockEnabled
        ? await getLowStockProducts(5, page, itemsPerPage)
        : await getProduct(page, itemsPerPage);
      const data = lowStockEnabled ? res?.data?.products : res?.data;
      setProducts(Array.isArray(data) ? data : []);
      if (!lowStockEnabled) {
        setSampleFileUrl(res?.data.SampleFileUrl || res?.data?.SampleFileUrl || "");
      }
      const pagination = lowStockEnabled ? res?.data?.pagination : res?.pagination;
      setTotalItems(pagination?.total || 0);
      setTotalPages(pagination?.totalPages || 1);
    } catch (err) {
      alert(getErrorMessage(err));
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts(currentPage);
  }, [currentPage, lowStockEnabled]);

  if (loading) {
    return <FullPageLoader text="Loading Products..." />;
  }

  // VIEW PRODUCT
  const handleViewProduct = (slug) => {
    navigate(`/product/view/${slug}`);
  };

  // DELETE PRODUCT
  const handleDeleteProduct = async (id) => {
    try {
      const confirmDelete = window.confirm("Are you sure you want to delete this product?");

      if (!confirmDelete) return;

      await deleteProduct(id);

      alert("Product deleted successfully");

      // REFRESH CURRENT PAGE
      fetchProducts(currentPage);
    } catch (error) {
      console.log(error);

      alert(getErrorMessage(error));
    }
  };

  return (
    <div className="container-fluid p-4 np-products-page">
      <div className="card p-3">
        <div className="d-flex align-items-center mb-3">
          <Checkbox
            checked={lowStockEnabled}
            onChange={(event) => {
              const nextSearchParams = new URLSearchParams(searchParams);
              if (event.target.checked) {
                nextSearchParams.set("lowStock", "true");
              } else {
                nextSearchParams.delete("lowStock");
              }
              setCurrentPage(1);
              setSearchParams(nextSearchParams);
            }}
          >
            Low Stock (5 or fewer units)
          </Checkbox>
        </div>
        {products.length === 0 ? (
          <EmptyState
            action={
              <div className="d-flex justify-content-center gap-2">
                <button
                  className="btn btn-primary"
                  onClick={() => navigate("/product/stock-adjust")}
                >
                  <i className="fa fa-sliders me-2"></i>
                  Stock Adjust
                </button>
                <button
                  className="btn btn-primary"
                  onClick={() => navigate("/product/add-stock")}
                >
                  <i className="fa fa-plus me-2"></i>
                  Add Stock
                </button>
                <button className="btn btn-primary" onClick={() => navigate("/product/add")}>
                  <i className="fa fa-plus me-2"></i>
                  Add Product
                </button>
              </div>
            }
          />
        ) : (
          <>
        <div className="d-flex align-items-center justify-content-end mb-3 gap-2">
          <ExportCsvButton onClick={handleExportProduct} />

          {/* Import CSV */}
          <button
            className="btn btn-success text-white"
            onClick={() =>
              navigate("/product/import", {
                state: {
                  sampleFileUrl,
                },
              })
            }
          >
            <i className="fa fa-upload me-2"></i>
            Import CSV
          </button>

            {/* Stock Adjust */}
            <button
              className="btn btn-primary"
              onClick={() => navigate("/product/stock-adjust")}
            >
              <i className="fa fa-sliders me-2"></i>
              Stock Adjust
            </button>
            {/* Add Stock */}
            <button className="btn btn-primary" onClick={() => navigate("/product/add-stock")}>
            <i className="fa fa-plus me-2"></i>
            Add Stock
          </button>
          {/* Add Product */}
          <button className="btn btn-primary" onClick={() => navigate("/product/add")}>
            <i className="fa fa-plus me-2"></i>
            Add Product
          </button>
        </div>

        {/* PRODUCT LIST */}
        <div className="row">
          {products.map((product) => (
            <div key={product._id} className="col-xl-4 col-lg-4 col-md-6 col-sm-12 mb-4">
              <div
                className="card h-100 border-0 shadow-sm np-product-card"
                style={{
                  overflow: "hidden",
                  transition: "0.3s",
                  cursor: "pointer",
                  borderRadius: "12px",
                }}
              >
                {/* IMAGE */}
                <div
                  style={{
                    height: "250px",
                    overflow: "hidden",
                    background: "#f8f8f8",
                    position: "relative",
                  }}
                >
                  <img
                    src={product?.seo?.metaImageUrl || "/default-product.png"}
                    alt={product?.productName}
                    className="w-100 h-100"
                    style={{
                      objectFit: "cover",
                      transition: "transform 0.3s ease",
                    }}
                    loading="lazy"
                    onMouseOver={(e) => {
                      e.currentTarget.style.transform = "scale(1.05)";
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.transform = "scale(1)";
                    }}
                  />

                  {/* TOP RIGHT ICONS */}
                  <div className="np-product-card__actions">
                    {/* VIEW */}
                    <ActionIconButton
                      icon={<FaEye />}
                      label="View"
                      variant="view"
                      onImage
                      onClick={(e) => {
                        e.preventDefault();
                        handleViewProduct(product.seo.slug);
                      }}
                    />

                    {/* EDIT */}
                    <ActionIconButton
                      icon={<FaEdit />}
                      label="Edit"
                      variant="edit"
                      onImage
                      onClick={(e) => {
                        e.preventDefault();

                        navigate(`/product/edit/${product.seo.slug}`, {
                          state: {
                            productId: product._id,
                            slug: product.seo.slug,
                          },
                        });
                      }}
                    />

                    {/* DELETE */}
                    <ActionIconButton
                      icon={<FaTrash />}
                      label="Delete"
                      variant="delete"
                      onImage
                      onClick={(e) => {
                        e.preventDefault();
                        handleDeleteProduct(product._id);
                      }}
                    />
                  </div>

                  {/* DISCOUNT */}
                  {product?.discountPercent > 0 && (
                    <span
                      className="badge badge-primary"
                      style={{
                        position: "absolute",
                        top: "10px",
                        left: "10px",
                        fontSize: "12px",
                        padding: "6px 10px",
                      }}
                    >
                      {product?.discountPercent}% OFF
                    </span>
                  )}
                </div>

                {/* CONTENT */}
                <div className="card-body d-flex flex-column">
                  {/* NAME */}
                  <h5
                    className="card-title mb-2"
                    style={{
                      fontWeight: "600",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      color: "#222",
                    }}
                  >
                    {product?.productName}
                  </h5>

                  {/* DESCRIPTION */}
                  <p
                    className="card-text text-muted"
                    style={{
                      fontSize: "14px",
                      minHeight: "40px",
                      overflow: "hidden",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                    }}
                  >
                    {product?.productDescription || "No description available"}
                  </p>

                  {/* PRICE */}
                  <div className="d-flex align-items-center mt-auto">
                    <h5
                      className="mb-0 mr-2"
                      style={{
                        color: "#28a745",
                        fontWeight: "700",
                      }}
                    >
                      ₹{product?.variants[0]?.price?.toLocaleString("en-IN") || 0}
                    </h5>

                    {/* {product?.mrp > product?.price && (
                    <span
                      style={{
                        textDecoration: "line-through",
                        color: "#999",
                        fontSize: "14px",
                      }}
                    >
                      ₹{product?.mrp?.toLocaleString("en-IN")}
                    </span>
                  )} */}
                  </div>
                  {Array.isArray(product?.variants) &&
                    product.variants.some(
                      (variant) => typeof variant?.totalStockAvailable === "number",
                    ) && (
                      <div className="mt-2">
                        {product.variants.map((variant, index) => {
                          if (typeof variant?.totalStockAvailable !== "number") return null;

                          const isLowStock = variant.totalStockAvailable <= 5;

                          return (
                            <div className="mb-1" key={variant?._id || index}>
                              <span
                                className={`badge ${
                                  isLowStock ? "badge-warning" : "badge-success"
                                }`}
                              >
                                Stock: {variant.totalStockAvailable}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* PAGINATION */}
        {totalItems > 0 && (
          <Pagination
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={(page) => setCurrentPage(page)}
          />
        )}
          </>
        )}
      </div>
    </div>
  );
}
